import { useState } from 'react';

import { getTaxInfoVerificationResult } from 'apis/financial-data-registry/api';
import {
  FuzzyMatchConfidenceLevel,
  TaxIdKey,
  TaxIdKeys,
  TaxInfoVerificationResult,
  VerificationResult,
} from 'apis/financial-data-registry/types';

import { taxVerificationConfiguration } from './taxVerificationConfiguration';
import { TaxInfoFormatValidationError } from '../errors';

export const taxIdKeyToLabelMap: Record<TaxIdKey, string> = {
  tin: 'TIN',
  vatId: 'VAT ID',
};

const isTaxInfoVerificationEnabledForCountry = (countryCode: string) => countryCode === 'FIN';

const dac7CountryList = [
  'AUT',
  'CYP',
  'CZE',
  'DEU',
  'DNK',
  'EST',
  'FIN',
  'GRC',
  'HRV',
  'LTU',
  'LUX',
  'LVA',
  'MLT',
  'POL',
  'SVK',
  'SVN',
  'SWE',
];

const extractInvalidFormatTaxIdsFromResult = (result: TaxInfoVerificationResult) => {
  return Object.entries(result).reduce(
    (acc, [key, value]) => (value?.isValidFormat === false ? [...acc, key as TaxIdKey] : acc),
    [] as TaxIdKeys,
  );
};

const getVerificationResultIgnoringError = async (merchantId: string): Promise<VerificationResult | null> => {
  return new Promise(async resolve => {
    try {
      resolve(
        await getTaxInfoVerificationResult({
          merchantId,
        }),
      );
    } catch {
      resolve(null);
    }
  });
};

const getVerificationResultByPolling = async (merchantId: string): Promise<VerificationResult | null> => {
  let attempts = 0;

  return new Promise(resolve => {
    const attemptGetVerificationResult = async () => {
      attempts += 1;

      const verificationResult = await getVerificationResultIgnoringError(merchantId);

      if (
        verificationResult?.status === 'COMPLETED' ||
        attempts === taxVerificationConfiguration.fetchResultsMaxAttempts
      ) {
        return resolve(verificationResult);
      }

      setTimeout(() => {
        attemptGetVerificationResult();
      }, taxVerificationConfiguration.fetchResultsPollingInterval);
    };

    attemptGetVerificationResult();
  });
};

const isDac7Country = (country: string) => dac7CountryList.includes(country);

const emptyTaxVerificationResult = {
  vatId: null,
  tin: null,
};

export const useTaxInfoVerification = (merchantId: string, countryCode: string) => {
  const [taxInfoVerificationResult, setTaxInfoVerificationResult] =
    useState<TaxInfoVerificationResult>(emptyTaxVerificationResult);
  const [isVerifyingTaxInfo, setIsVerifyingTaxInfo] = useState(false);

  const startTaxInfoVerification = async (): Promise<FuzzyMatchConfidenceLevel> => {
    setIsVerifyingTaxInfo(true);

    const verificationResult = await getVerificationResultByPolling(merchantId);

    setIsVerifyingTaxInfo(false);

    if (verificationResult?.status === 'COMPLETED' && verificationResult.result) {
      const { result } = verificationResult;

      if (result.vatId?.isValidFormat === false || result.tin?.isValidFormat === false) {
        throw new TaxInfoFormatValidationError(extractInvalidFormatTaxIdsFromResult(result));
      }

      const isVatResultInconclusive = [result.vatId?.name?.status, result.vatId?.address?.status].includes(
        'INCONCLUSIVE',
      );

      const isTinResultInconclusive = [result.tin?.name?.status, result.tin?.address?.status].includes('INCONCLUSIVE');

      if (isVatResultInconclusive || isTinResultInconclusive) {
        const vatIdResult = { vatId: isVatResultInconclusive ? result.vatId : null };
        const tinResult = { tin: isTinResultInconclusive ? result.tin : null };

        setTaxInfoVerificationResult({
          ...vatIdResult,
          ...tinResult,
        });

        return 'INCONCLUSIVE';
      }
    }

    return 'PASS';
  };

  const isTaxInfoVerificationInconclusive = !!taxInfoVerificationResult?.vatId || !!taxInfoVerificationResult?.tin;
  const isTaxInfoVerificationEnabled = isTaxInfoVerificationEnabledForCountry(countryCode);

  return {
    isVerifyingTaxInfo,
    isTaxInfoVerificationInconclusive,
    isTaxInfoVerificationEnabled,
    taxInfoVerificationResult,
    resetTaxInfoVerificationResult: () => setTaxInfoVerificationResult(emptyTaxVerificationResult),
    startTaxInfoVerification,
    isDac7Country,
  };
};
